import { useDeferredValue, useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { api, errMsg } from '../../lib/api';
import TemplateThumb from '../../components/TemplateThumb';
import TemplateBackground from '../../components/TemplateBackground';
import { BUILTIN_BG } from '../../lib/templateBackgrounds';
import { ALL_BLOCKS, BLOCK_LABELS, resolveBlocks } from '../../components/blocks/ContentBlocks';

interface TemplateItem {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  /** 实际渲染用的内置布局 key；为空时等同 slug */
  layout?: string | null;
  /** 后台自定义的背景 SVG；为空则前端使用内置默认背景 */
  bgSvg?: string | null;
  /** 区块配置 JSON：{"blocks":["album","tributes","articles"]}，控制区块启用与顺序 */
  config: string | null;
  /** 内置模板不可删除，且可作为自定义模板的基础布局 */
  isBuiltin: boolean;
  isEnabled: boolean;
  sortOrder: number;
}

interface ThemeItem {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  config: Record<string, string>;
  isEnabled: boolean;
  sortOrder: number;
}

export default function AdminTemplates() {
  const [templates, setTemplates] = useState<TemplateItem[]>([]);
  const [themes, setThemes] = useState<ThemeItem[]>([]);
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(true);
  /** 正在编辑背景的模板 id，以及编辑框草稿 */
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  /** 区块配置：正在编辑的模板 id，以及草稿列表 */
  const [blockEdit, setBlockEdit] = useState<string | null>(null);
  const [blockDraft, setBlockDraft] = useState<string[]>([]);
  /** 新增模板：展开状态与表单 */
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: '',
    slug: '',
    layout: 'classic',
    description: '',
    sortOrder: 999,
  });

  async function load() {
    setLoading(true);
    const [t, th] = await Promise.all([
      api<{ items: TemplateItem[] }>('/admin/templates'),
      api<{ items: ThemeItem[] }>('/admin/themes'),
    ]);
    if (t.ok && t.data) setTemplates(t.data.items);
    if (th.ok && th.data) setThemes(th.data.items);
    setLoading(false);
  }

  useEffect(() => {
    load();
  }, []);

  async function toggleTemplate(t: TemplateItem) {
    await api(`/admin/templates/${t.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ isEnabled: !t.isEnabled }),
    });
    setNotice(`「${t.name}」已${t.isEnabled ? '下架' : '启用'}`);
    load();
  }

  /** 当前正在编辑背景的模板（弹窗打开时非空） */
  const editingItem = templates.find((x) => x.id === editing) ?? null;

  async function saveBg(t: TemplateItem) {
    const res = await api(`/admin/templates/${t.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ bgSvg: draft }),
    });
    if (!res.ok) {
      setNotice(`保存失败：${errMsg(res, '未知错误')}`);
      return;
    }
    setTemplates((prev) => prev.map((x) => (x.id === t.id ? { ...x, bgSvg: draft } : x)));
    setNotice(`「${t.name}」背景已保存`);
    setEditing(null);
    load(); // 以服务端返回为准重新拉取
  }

  /** 清空即恢复内置默认背景（前端在 bgSvg 为空时回落到内置 SVG） */
  async function restoreBg(t: TemplateItem) {
    const res = await api(`/admin/templates/${t.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ bgSvg: '' }),
    });
    if (!res.ok) {
      setNotice(`恢复失败：${errMsg(res, '未知错误')}`);
      return;
    }
    setTemplates((prev) => prev.map((x) => (x.id === t.id ? { ...x, bgSvg: '' } : x)));
    setDraft('');
    setNotice(`「${t.name}」背景已恢复默认`);
    setEditing(null);
    load();
  }

  /** 新增模板：布局复用内置组件，因此无需改代码/发版即可扩展模板 */
  async function createTemplate() {
    const res = await api('/admin/templates', {
      method: 'POST',
      body: JSON.stringify({
        name: form.name,
        slug: form.slug,
        layout: form.layout,
        description: form.description || undefined,
        sortOrder: form.sortOrder,
        isEnabled: true,
      }),
    });
    if (!res.ok) {
      setNotice(`创建失败：${errMsg(res, '未知错误')}`);
      return;
    }
    setNotice(`模板「${form.name}」已创建`);
    setForm({ name: '', slug: '', layout: 'classic', description: '', sortOrder: 999 });
    setCreating(false);
    load();
  }

  /** 删除模板：仅自定义模板可删；已使用它的纪念馆会回落到经典模板 */
  async function deleteTemplate(t: TemplateItem) {
    if (!window.confirm(`确定删除模板「${t.name}」？已使用它的纪念馆将回落到经典模板。`)) return;
    const res = await api(`/admin/templates/${t.id}`, { method: 'DELETE' });
    if (!res.ok) {
      setNotice(`删除失败：${errMsg(res, '未知错误')}`);
      return;
    }
    setNotice(`模板「${t.name}」已删除`);
    load();
  }

  function openBlocks(t: TemplateItem) {
    setBlockEdit(t.id);
    setBlockDraft(resolveBlocks(t.config));
  }

  function toggleBlock(b: string) {
    setBlockDraft((prev) =>
      prev.includes(b) ? prev.filter((x) => x !== b) : [...prev, b]
    );
  }

  function moveBlock(i: number, dir: -1 | 1) {
    setBlockDraft((prev) => {
      const j = i + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  }

  async function saveBlocks(t: TemplateItem) {
    const config = JSON.stringify({ blocks: blockDraft });
    const res = await api(`/admin/templates/${t.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ config }),
    });
    if (!res.ok) {
      setNotice(`保存失败：${errMsg(res, '未知错误')}`);
      return;
    }
    setTemplates((prev) => prev.map((x) => (x.id === t.id ? { ...x, config } : x)));
    setNotice(`「${t.name}」区块配置已保存`);
    setBlockEdit(null);
    load();
  }

  async function toggleTheme(th: ThemeItem) {
    await api(`/admin/themes/${th.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ isEnabled: !th.isEnabled }),
    });
    setNotice(`「${th.name}」已${th.isEnabled ? '下架' : '启用'}`);
    load();
  }

  if (loading) return <p className="text-sm text-ink-500">加载中…</p>;

  return (
    <div>
      {notice && (
        <p className="mb-5 rounded-lg bg-candle-300/20 px-4 py-2.5 text-sm text-ink-700">
          {notice}
        </p>
      )}

      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-sm font-medium text-ink-900">页面模板（{templates.length}）</h2>
        <button
          onClick={() => setCreating((v) => !v)}
          className="rounded-full bg-ink-900 px-3 py-1 text-[11px] text-white"
        >
          {creating ? '取消新增' : '+ 新增模板'}
        </button>
      </div>

      {creating && (
        <div className="mb-5 rounded-xl border border-ink-200 bg-ink-50/60 p-4">
          <p className="mb-3 text-xs font-medium text-ink-800">
            新增模板（布局复用内置组件，可另配专属背景 SVG）
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-[11px] text-ink-600">名称</span>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full rounded-md border border-ink-200 bg-white px-2 py-1.5 text-xs outline-none"
                placeholder="如：经典追思·清明版"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] text-ink-600">
                标识（小写字母/数字/连字符）
              </span>
              <input
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                className="w-full rounded-md border border-ink-200 bg-white px-2 py-1.5 font-mono text-xs outline-none"
                placeholder="classic-qingming"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] text-ink-600">基础布局</span>
              <select
                value={form.layout}
                onChange={(e) => setForm({ ...form, layout: e.target.value })}
                className="w-full rounded-md border border-ink-200 bg-white px-2 py-1.5 text-xs outline-none"
              >
                {templates
                  .filter((x) => x.isBuiltin)
                  .map((x) => (
                    <option key={x.id} value={x.layout || x.slug}>
                      {x.name}
                    </option>
                  ))}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] text-ink-600">排序</span>
              <input
                type="number"
                value={form.sortOrder}
                onChange={(e) => setForm({ ...form, sortOrder: Number(e.target.value) })}
                className="w-full rounded-md border border-ink-200 bg-white px-2 py-1.5 text-xs outline-none"
              />
            </label>
          </div>
          <label className="mt-3 block">
            <span className="mb-1 block text-[11px] text-ink-600">描述（可选）</span>
            <input
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full rounded-md border border-ink-200 bg-white px-2 py-1.5 text-xs outline-none"
            />
          </label>
          <button
            onClick={createTemplate}
            className="mt-3 rounded-full bg-ink-900 px-4 py-1.5 text-xs text-white"
          >
            创建
          </button>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {templates.map((t) => (
          <div
            key={t.id}
            className={`rounded-2xl border bg-white p-4 ${
              t.isEnabled ? 'border-ink-200' : 'border-red-200 bg-red-50/40'
            }`}
          >
            <div className="mb-3 text-ink-800">
              <TemplateThumb slug={t.slug} layout={t.layout} bgSvg={t.bgSvg} />
            </div>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm text-ink-900">{t.name}</p>
                <p className="mt-0.5 font-mono text-[10px] text-ink-400">{t.slug}</p>
                <p className="mt-1 text-[11px] text-ink-500">排序 {t.sortOrder}</p>
              </div>
              <button
                onClick={() => toggleTemplate(t)}
                className={`shrink-0 rounded-full px-3 py-1 text-[10px] ${
                  t.isEnabled
                    ? 'bg-emerald-50 text-emerald-700'
                    : 'bg-red-50 text-red-600'
                }`}
              >
                {t.isEnabled ? '已启用' : '已下架'}
              </button>
            </div>
            <p className="mt-2 text-[10px] text-ink-500">基础布局：{t.layout || t.slug}</p>
            {!t.isBuiltin && (
              <button
                onClick={() => deleteTemplate(t)}
                className="mt-1 rounded-full bg-red-50 px-3 py-1 text-[10px] text-red-600"
              >
                删除模板
              </button>
            )}
            <button
              onClick={() => openBlocks(t)}
              className="mt-2 rounded-full bg-ink-100 px-3 py-1 text-[10px] text-ink-700 hover:bg-ink-200"
            >
              区块配置
            </button>
            {blockEdit === t.id && (
              <div className="mt-2 rounded-lg border border-ink-200 bg-ink-50/60 p-2">
                <p className="mb-1 text-[10px] text-ink-500">勾选启用的区块，用 ↑↓ 调整顺序</p>
                {ALL_BLOCKS.map((b) => {
                  const on = blockDraft.includes(b);
                  const idx = blockDraft.indexOf(b);
                  return (
                    <div key={b} className="flex items-center justify-between gap-2 py-0.5">
                      <label className="flex items-center gap-1.5 text-[11px] text-ink-700">
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() => toggleBlock(b)}
                        />
                        {BLOCK_LABELS[b]}
                      </label>
                      {on && (
                        <span className="flex gap-1">
                          <button
                            onClick={() => moveBlock(idx, -1)}
                            className="rounded bg-white px-1.5 text-[10px]"
                          >
                            ↑
                          </button>
                          <button
                            onClick={() => moveBlock(idx, 1)}
                            className="rounded bg-white px-1.5 text-[10px]"
                          >
                            ↓
                          </button>
                        </span>
                      )}
                    </div>
                  );
                })}
                <div className="mt-2 flex gap-2">
                  <button
                    onClick={() => saveBlocks(t)}
                    className="rounded-full bg-ink-900 px-3 py-1 text-[10px] text-white"
                  >
                    保存
                  </button>
                  <button
                    onClick={() => setBlockEdit(null)}
                    className="rounded-full px-3 py-1 text-[10px] text-ink-500"
                  >
                    取消
                  </button>
                </div>
              </div>
            )}
            <button
              onClick={() => {
                setEditing(t.id);
                setDraft(t.bgSvg ?? '');
              }}
              className="mt-2 rounded-full bg-ink-100 px-3 py-1 text-[10px] text-ink-700 hover:bg-ink-200"
            >
              编辑背景 SVG
            </button>
            {t.description && (
              <p className="mt-2 line-clamp-2 text-[11px] leading-relaxed text-ink-500">
                {t.description}
              </p>
            )}
          </div>
        ))}
      </div>

      <h2 className="mb-4 mt-10 text-sm font-medium text-ink-900">配色主题（{themes.length}）</h2>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {themes.map((th) => (
          <div
            key={th.id}
            className={`rounded-2xl border bg-white p-4 ${
              th.isEnabled ? 'border-ink-200' : 'border-red-200 bg-red-50/40'
            }`}
          >
            <div className="mb-3 flex gap-2">
              {['--c-bg', '--c-accent', '--c-text'].map((k) => (
                <span
                  key={k}
                  className="h-10 flex-1 rounded-lg border border-ink-200"
                  style={{ background: th.config[k] }}
                />
              ))}
            </div>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm text-ink-900">{th.name}</p>
                <p className="mt-0.5 font-mono text-[10px] text-ink-400">{th.slug}</p>
              </div>
              <button
                onClick={() => toggleTheme(th)}
                className={`shrink-0 rounded-full px-3 py-1 text-[10px] ${
                  th.isEnabled
                    ? 'bg-emerald-50 text-emerald-700'
                    : 'bg-red-50 text-red-600'
                }`}
              >
                {th.isEnabled ? '已启用' : '已下架'}
              </button>
            </div>
            {th.description && (
              <p className="mt-2 line-clamp-2 text-[11px] leading-relaxed text-ink-500">
                {th.description}
              </p>
            )}
          </div>
        ))}
      </div>

      <p className="mt-8 text-[11px] leading-relaxed text-ink-400">
        提示：下架的模板或配色不会出现在馆主的选择列表，但已使用它的纪念馆不受影响。
        <br />
        修改配色后，所有使用该配色的纪念馆会立即生效。
      </p>

      {editingItem && (
        <BgEditorModal
          t={editingItem}
          draft={draft}
          setDraft={setDraft}
          onClose={() => setEditing(null)}
          onSave={saveBg}
          onRestore={restoreBg}
        />
      )}
    </div>
  );
}

/** 预览区模拟纪念馆配色，让 SVG 里的 var(--c-*) 取到真实值 */
const PREVIEW_VARS: Record<string, string> = {
  '--c-bg': '#faf9f7',
  '--c-surface': '#ffffff',
  '--c-text': '#1c1917',
  '--c-muted': '#867c68',
  '--c-accent': '#d97706',
  '--c-border': '#e3dfd7',
  '--c-bg-grad': '#eef0f2',
  '--c-accent-soft': '#ffd894',
};

/**
 * 背景 SVG 编辑弹窗。
 * 左侧改代码、右侧即时渲染：粘贴/微调时无需反复保存就能看到实际效果。
 * 留空即代表「使用内置默认背景」，此时右侧预览显示的正是内置背景。
 */
function BgEditorModal({
  t,
  draft,
  setDraft,
  onClose,
  onSave,
  onRestore,
}: {
  t: TemplateItem;
  draft: string;
  setDraft: (v: string) => void;
  onClose: () => void;
  onSave: (t: TemplateItem) => void;
  onRestore: (t: TemplateItem) => void;
}) {
  // 预览走延迟值：长 SVG 重渲染不阻塞按键，输入保持顺滑
  const preview = useDeferredValue(draft);
  const layoutKey = t.layout || t.slug;
  const builtin = BUILTIN_BG[layoutKey];

  // Esc 关闭
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-ink-900/40" onClick={onClose} />

      <div className="relative flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-ink-200 bg-white shadow-xl">
        <div className="flex items-start justify-between gap-4 border-b border-ink-200 px-5 py-3">
          <div className="min-w-0">
            <p className="text-sm font-medium text-ink-900">编辑背景 SVG · {t.name}</p>
            <p className="mt-0.5 text-[11px] leading-relaxed text-ink-500">
              左侧改代码，右侧立即预览 · 基础布局 {layoutKey}
              {!t.bgSvg && ' · 当前未自定义，预览显示的是内置默认'}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="关闭"
            className="shrink-0 rounded-full px-2 py-1 text-sm text-ink-500 hover:bg-ink-100"
          >
            ✕
          </button>
        </div>

        <div className="grid flex-1 gap-4 overflow-hidden p-5 md:grid-cols-2">
          {/* 左：代码编辑 */}
          <div className="flex min-h-0 flex-col">
            <div className="mb-1.5 flex items-center justify-between gap-2">
              <p className="text-[11px] text-ink-600">SVG 代码</p>
              <div className="flex gap-1.5">
                {builtin && (
                  <button
                    onClick={() => setDraft(builtin)}
                    className="rounded-full bg-candle-300/40 px-2.5 py-1 text-[10px] text-ink-800 transition hover:bg-candle-300/70"
                  >
                    导入内置
                  </button>
                )}
                <button
                  onClick={() => setDraft('')}
                  className="rounded-full bg-ink-100 px-2.5 py-1 text-[10px] text-ink-700 transition hover:bg-ink-200"
                >
                  清空
                </button>
              </div>
            </div>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              spellCheck={false}
              placeholder={'粘贴 <svg>…</svg>（仅 SMIL 动画；留空则使用内置默认背景）'}
              className="min-h-[300px] w-full flex-1 resize-none rounded-lg border border-ink-200 bg-ink-50/60 p-3 font-mono text-[11px] leading-relaxed outline-none focus:border-ink-500 focus:bg-white"
            />
            <p className="mt-1.5 text-[10px] leading-relaxed text-ink-500">
              根节点必须是 &lt;svg&gt;；&lt;script&gt; 与事件属性会被自动过滤。留空即恢复内置默认背景。
            </p>
          </div>

          {/* 右：实时预览 */}
          <div className="flex min-h-0 flex-col">
            <p className="mb-1.5 text-[11px] text-ink-600">实时预览</p>
            <div
              className="relative isolate min-h-[300px] flex-1 overflow-hidden rounded-lg border border-ink-200 bg-ink-100"
              style={PREVIEW_VARS as unknown as CSSProperties}
            >
              <TemplateBackground slug={layoutKey} bgSvg={preview} />
              <div className="pointer-events-none absolute left-1/2 top-6 -translate-x-1/2">
                <div className="h-20 w-20 rounded-full border-2 border-ink-400 bg-white/70 shadow" />
              </div>
            </div>
            <p className="mt-1.5 text-[10px] leading-relaxed text-ink-500">
              灰圈＝头像占位示意；实际页面会套用该纪念馆所选配色。
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-ink-200 bg-ink-50/60 px-5 py-3">
          <button
            onClick={() => onRestore(t)}
            className="rounded-full bg-ink-100 px-4 py-1.5 text-xs text-ink-700 transition hover:bg-ink-200"
          >
            恢复默认
          </button>
          <button
            onClick={onClose}
            className="rounded-full px-4 py-1.5 text-xs text-ink-500 transition hover:bg-ink-100"
          >
            取消
          </button>
          <button
            onClick={() => onSave(t)}
            className="rounded-full bg-ink-900 px-5 py-1.5 text-xs text-ink-50 transition hover:bg-ink-800"
          >
            保存
          </button>
        </div>
      </div>
    </div>
  );
}
